//--------------- CARD + MODAL DE CONFIGURAÇÃO DO RESEND ---------------//
// E-mail transacional por empresa. A chave fica cifrada no backend; aqui só
// aparece o hint (últimos 8 caracteres). O nó "Email" das automações usa
// essa configuração.
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Mail, Settings2, CheckCircle2, AlertTriangle, Loader2, Copy, RefreshCw,
  Send, ShieldCheck, Globe, X,
} from "lucide-react";
import emailConfigService from "../services/emailConfigService";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function StatusPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
      ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-amber-500/10 border-amber-500/30 text-amber-200"
    }`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {label}
    </span>
  );
}

export default function ResendCard() {
  const [dados, setDados] = useState(null); // { config, webhook_url }
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState(false);

  const carregar = () => {
    setCarregando(true);
    return emailConfigService.obter()
      .then((r) => setDados(r.data))
      .catch(() => toast.error("Erro ao carregar configuração do Resend"))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { carregar(); }, []);

  const cfg = dados?.config;
  const configurado = Boolean(cfg?.tem_api_key && cfg?.remetente_email && cfg?.ativo);

  return (
    <>
      <div className="mb-8">
        <div className="label-overline mb-2">E-mail transacional</div>
        <div className="surface-card p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/30 grid place-items-center shrink-0">
            <Mail className="w-5 h-5 text-blue-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-white">Resend</span>
              {carregando ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-4" />
              ) : (
                <StatusPill ok={configurado} label={configurado ? "configurado" : "não configurado"} />
              )}
              {!carregando && cfg && (
                <StatusPill ok={cfg.tem_webhook_secret} label={cfg.tem_webhook_secret ? "webhook assinado" : "webhook sem assinatura"} />
              )}
            </div>
            <div className="text-sm text-ink-3 mt-0.5 truncate">
              {configurado
                ? <>Remetente <b className="text-ink-2">{cfg.remetente_nome ? `${cfg.remetente_nome} <${cfg.remetente_email}>` : cfg.remetente_email}</b>{cfg.api_key_hint ? ` · chave …${cfg.api_key_hint}` : ""}</>
                : "Configure a chave da API e o remetente pra usar o nó Email nas automações."}
            </div>
          </div>
          <button
            onClick={() => setAberto(true)}
            className="btn-primary !py-2 !px-4 inline-flex items-center gap-2 shrink-0"
          >
            <Settings2 className="w-4 h-4" /> {configurado ? "Editar" : "Configurar"}
          </button>
        </div>
      </div>

      {aberto && (
        <ModalResend
          dados={dados}
          onClose={() => setAberto(false)}
          onSaved={(novo) => { setDados(novo); }}
        />
      )}
    </>
  );
}

function ModalResend({ dados, onClose, onSaved }) {
  const cfg = dados?.config || {};
  const [apiKey, setApiKey] = useState("");
  const [remetenteNome, setRemetenteNome] = useState(cfg.remetente_nome || "");
  const [remetenteEmail, setRemetenteEmail] = useState(cfg.remetente_email || "");
  const [replyTo, setReplyTo] = useState(cfg.reply_to || "");
  const [ativo, setAtivo] = useState(cfg.ativo !== false);
  const [webhookSecret, setWebhookSecret] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [dominios, setDominios] = useState(null);
  const [carregandoDominios, setCarregandoDominios] = useState(false);
  const [criandoWebhook, setCriandoWebhook] = useState(false);
  const [testePara, setTestePara] = useState("");
  const [testando, setTestando] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(dados?.webhook_url || null);
  const [temKey, setTemKey] = useState(Boolean(cfg.tem_api_key));
  const [temSecret, setTemSecret] = useState(Boolean(cfg.tem_webhook_secret));

  const carregarDominios = async () => {
    setCarregandoDominios(true);
    try {
      const r = await emailConfigService.dominios();
      setDominios(r.data?.dominios || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao listar domínios");
      setDominios([]);
    } finally {
      setCarregandoDominios(false);
    }
  };

  useEffect(() => { if (temKey) carregarDominios(); /* eslint-disable-line */ }, []);

  const salvar = async () => {
    if (remetenteEmail && !EMAIL_RE.test(remetenteEmail)) { toast.error("E-mail do remetente inválido"); return; }
    if (replyTo && !EMAIL_RE.test(replyTo)) { toast.error("Reply-to inválido"); return; }
    setSalvando(true);
    try {
      const payload = { remetente_nome: remetenteNome, remetente_email: remetenteEmail, reply_to: replyTo, ativo };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      if (webhookSecret.trim()) payload.webhook_secret = webhookSecret.trim();
      const r = await emailConfigService.salvar(payload);
      onSaved?.(r.data);
      setWebhookUrl(r.data?.webhook_url || webhookUrl);
      setTemKey(Boolean(r.data?.config?.tem_api_key));
      setTemSecret(Boolean(r.data?.config?.tem_webhook_secret));
      setApiKey(""); setWebhookSecret("");
      toast.success("Configuração do Resend salva");
      if (r.data?.config?.tem_api_key) carregarDominios();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao salvar");
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const garantirWebhook = async () => {
    setCriandoWebhook(true);
    try {
      if (apiKey.trim() || !temKey) {
        const ok = await salvar();
        if (!ok) return;
      }
      const r = await emailConfigService.garantirWebhook();
      setTemSecret(Boolean(r.data?.config?.tem_webhook_secret));
      onSaved?.({ config: r.data?.config, webhook_url: r.data?.webhook_url });
      toast.success(r.data?.criado ? "Webhook criado no Resend (todos os eventos de e-mail)" : "Webhook já existia no Resend");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao criar webhook");
    } finally {
      setCriandoWebhook(false);
    }
  };

  const testar = async () => {
    if (!EMAIL_RE.test(testePara)) { toast.error("Informe um e-mail válido"); return; }
    setTestando(true);
    try {
      if (apiKey.trim() || remetenteEmail !== (cfg.remetente_email || "")) {
        const ok = await salvar();
        if (!ok) return;
      }
      await emailConfigService.testar(testePara.trim());
      toast.success(`E-mail de teste enviado pra ${testePara.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Falha no envio de teste", { autoClose: 8000 });
    } finally {
      setTestando(false);
    }
  };

  const [alterandoRastreio, setAlterandoRastreio] = useState(null);
  const alternarRastreio = async (d, ligar) => {
    setAlterandoRastreio(d.id);
    try {
      const r = await emailConfigService.rastreio(d.id, { open: ligar, click: ligar });
      setDominios((ds) => ds.map((x) => (x.id === d.id ? r.data : x)));
      if (ligar && !r.data.rastreio_ativo) toast.warn("Rastreio ligado. Falta criar o CNAME de tracking no DNS e clicar em verificar.", { autoClose: 10000 });
      else toast.success(ligar ? `Rastreio de abertura e clique ativo em ${d.nome}` : `Rastreio desligado em ${d.nome}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao alterar rastreio");
    } finally {
      setAlterandoRastreio(null);
    }
  };

  const [verificando, setVerificando] = useState(null);
  const verificarDominio = async (d) => {
    setVerificando(d.id);
    try {
      const r = await emailConfigService.verificarDominio(d.id);
      setDominios((ds) => ds.map((x) => (x.id === d.id ? r.data : x)));
      if (r.data.rastreio_ativo) toast.success(`Rastreio verificado em ${d.nome}`);
      else toast.info("Verificação pedida. O DNS pode levar alguns minutos — clique em atualizar depois.", { autoClose: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao verificar domínio");
    } finally {
      setVerificando(null);
    }
  };

  const copiar = (txt) => {
    navigator.clipboard?.writeText(txt).then(() => toast.info("Copiado"));
  };

  const dominioRemetente = remetenteEmail.split("@")[1]?.toLowerCase();
  const dominioVerificado = dominios?.some((d) => d.nome?.toLowerCase() === dominioRemetente && d.status === "verified");

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-base border border-white/[0.06] rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-white/[0.04] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/30 grid place-items-center">
              <Mail className="w-4 h-4 text-blue-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Resend — e-mail transacional</div>
              <div className="text-[11px] text-ink-4">Chave fica cifrada no servidor. Só o final é exibido.</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-1 text-ink-4 hover:text-ink-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* API KEY */}
          <div>
            <label className="block text-xs text-ink-2 font-medium mb-1">API key do Resend</label>
            <div className="text-[11px] text-ink-4 mb-2">
              {temKey ? <>Chave salva (…{cfg.api_key_hint}). Cole uma nova só se quiser trocar.</> : <>Crie em resend.com → API Keys (permissão <i>Full access</i>, pra criar o webhook por aqui).</>}
            </div>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={temKey ? "••••••••••••" : "re_xxxxxxxxxxxxxxxxx"}
              className="w-full bg-surface-1 border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none"
              autoComplete="off"
            />
          </div>

          {/* REMETENTE */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-2 font-medium mb-1">Nome do remetente</label>
              <input value={remetenteNome} onChange={(e) => setRemetenteNome(e.target.value)} placeholder="Metriqa"
                className="w-full bg-surface-1 border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none" />
            </div>
            <div>
              <label className="block text-xs text-ink-2 font-medium mb-1">E-mail do remetente</label>
              <input value={remetenteEmail} onChange={(e) => setRemetenteEmail(e.target.value)} placeholder="contato@seudominio.com.br"
                className="w-full bg-surface-1 border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none" />
            </div>
            <div>
              <label className="block text-xs text-ink-2 font-medium mb-1">Reply-to (opcional)</label>
              <input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="suporte@seudominio.com.br"
                className="w-full bg-surface-1 border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none" />
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-2 mt-5 cursor-pointer">
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} className="accent-emerald-500" />
              Envio de e-mail ativo
            </label>
          </div>

          {/* DOMÍNIOS */}
          <div className="p-3 rounded-lg bg-surface-1 border border-white/[0.06]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-ink-2 font-medium inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> Domínios na conta Resend</div>
              <button onClick={carregarDominios} disabled={!temKey || carregandoDominios} className="text-[11px] text-ink-3 hover:text-ink-1 inline-flex items-center gap-1 disabled:opacity-50">
                {carregandoDominios ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} atualizar
              </button>
            </div>
            {!temKey ? (
              <div className="text-[11px] text-ink-4">Salve a API key pra listar os domínios.</div>
            ) : dominios == null ? (
              <div className="text-[11px] text-ink-4">Carregando…</div>
            ) : dominios.length === 0 ? (
              <div className="text-[11px] text-amber-200">Nenhum domínio na conta. Adicione em resend.com → Domains e crie os registros DNS (SPF/DKIM) na Cloudflare.</div>
            ) : (<>
              <ul className="space-y-1.5">
                {dominios.map((d) => {
                  const ligado = d.open_tracking && d.click_tracking;
                  const pendenteDns = ligado && !d.rastreio_ativo && d.tracking_record;
                  return (
                    <li key={d.id} className="text-xs space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-ink-1 truncate">{d.nome}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <StatusPill ok={d.status === "verified"} label={d.status} />
                          <StatusPill ok={d.rastreio_ativo} label={d.rastreio_ativo ? "rastreio ativo" : ligado ? "aguardando DNS" : "sem rastreio"} />
                          <button
                            onClick={() => alternarRastreio(d, !ligado)}
                            disabled={alterandoRastreio === d.id}
                            className="text-[11px] px-2 py-0.5 rounded border border-white/[0.08] text-ink-2 hover:bg-surface-2 disabled:opacity-50"
                            title="Sem rastreio o Resend não envia os eventos de abertura/clique — o 'lido' não existe"
                          >
                            {alterandoRastreio === d.id ? "…" : ligado ? "desligar" : "ligar rastreio"}
                          </button>
                        </div>
                      </div>
                      {pendenteDns && (
                        <div className="ml-2 p-2 rounded bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-100 space-y-1">
                          <div>Crie este registro no DNS de <b>{d.nome.split(".").slice(1).join(".")}</b> e clique em verificar:</div>
                          <div className="flex items-center gap-2 font-mono">
                            <span className="text-ink-3">{d.tracking_record.type}</span>
                            <span className="truncate">{d.tracking_record.name}</span>
                            <span className="text-ink-3">→</span>
                            <span className="truncate">{d.tracking_record.value}</span>
                            <button onClick={() => copiar(`${d.tracking_record.name} CNAME ${d.tracking_record.value}`)} className="p-1 rounded hover:bg-surface-2 text-ink-3" title="Copiar"><Copy className="w-3 h-3" /></button>
                          </div>
                          <button
                            onClick={() => verificarDominio(d)}
                            disabled={verificando === d.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-500/30 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50"
                          >
                            {verificando === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} verificar DNS
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-2 text-[11px] text-ink-4">
                "Lido" = evento de abertura do Resend. Só existe com o rastreio ligado no domínio do remetente. Clientes de e-mail que bloqueiam imagens (ou Apple Mail Privacy) podem não contar.
              </div>
            </>)}
            {dominioRemetente && dominios && dominios.length > 0 && !dominioVerificado && (
              <div className="mt-2 text-[11px] text-amber-200 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> O domínio do remetente ({dominioRemetente}) não está verificado — o Resend vai recusar o envio.
              </div>
            )}
          </div>

          {/* WEBHOOK */}
          <div className="p-3 rounded-lg bg-surface-1 border border-white/[0.06] space-y-2">
            <div className="text-xs text-ink-2 font-medium inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Webhook de eventos (entregue, aberto, clique, bounce, spam)</div>
            {webhookUrl ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-[11px] text-ink-2 bg-base border border-white/[0.06] rounded px-2 py-1">{webhookUrl}</code>
                <button onClick={() => copiar(webhookUrl)} className="p-1.5 rounded hover:bg-surface-2 text-ink-3" title="Copiar"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="text-[11px] text-ink-4">Salve a configuração pra gerar a URL do webhook.</div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <button
                onClick={garantirWebhook}
                disabled={criandoWebhook || (!temKey && !apiKey.trim())}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-200 text-xs hover:bg-blue-500/25 disabled:opacity-50"
              >
                {criandoWebhook ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                Criar webhook no Resend automaticamente
              </button>
              <span className="text-[11px] text-ink-4">{temSecret ? `Assinatura salva (…${cfg.webhook_secret_hint || ""}).` : "Cria o endpoint na sua conta e guarda a assinatura."}</span>
            </div>
            <details className="text-[11px] text-ink-4">
              <summary className="cursor-pointer hover:text-ink-2">Criei o webhook manualmente — colar o signing secret</summary>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="whsec_..."
                className="mt-2 w-full bg-base border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none text-ink-1"
                autoComplete="off"
              />
              <div className="mt-1">Abertos/cliques dependem do rastreio ligado no domínio (lista acima).</div>
            </details>
          </div>

          {/* TESTE */}
          <div className="p-3 rounded-lg bg-surface-1 border border-white/[0.06]">
            <div className="text-xs text-ink-2 font-medium mb-2 inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Enviar e-mail de teste</div>
            <div className="flex gap-2">
              <input value={testePara} onChange={(e) => setTestePara(e.target.value)} placeholder="seu@email.com"
                className="flex-1 bg-base border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none" />
              <button
                onClick={testar}
                disabled={testando || !testePara.trim() || (!temKey && !apiKey.trim()) || !remetenteEmail}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-surface-2 border border-white/[0.08] text-ink-1 text-xs hover:bg-surface-3 disabled:opacity-50"
              >
                {testando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Testar
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-white/[0.04] flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-ink-3 hover:bg-surface-1">Fechar</button>
          <button onClick={salvar} disabled={salvando} className="btn-primary !py-1.5 !px-4 inline-flex items-center gap-2 disabled:opacity-60">
            {salvando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
